import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { canonicalUrl, DEFAULT_OG_ALT, DEFAULT_OG_IMAGE, getSeoEntry, structuredDataFor } from "../seo-data";

export function Seo({ title, description }: { title: string; description: string }) {
  const { pathname } = useLocation();
  useEffect(() => {
    const entry = getSeoEntry(pathname);
    const effectiveTitle = entry.title || title;
    const effectiveDescription = entry.description || description;
    const canonical = canonicalUrl(entry.path);
    document.title = effectiveTitle;

    const setMeta = (selector: string, attribute: "name" | "property", key: string, content: string) => {
      let element = document.querySelector<HTMLMetaElement>(selector);
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attribute, key);
        document.head.append(element);
      }
      element.content = content;
    };
    const setLink = (rel: string, href: string) => {
      let element = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
      if (!element) {
        element = document.createElement("link");
        element.rel = rel;
        document.head.append(element);
      }
      element.href = href;
    };

    setMeta('meta[name="description"]', "name", "description", effectiveDescription);
    setMeta('meta[name="robots"]', "name", "robots", entry.noindex ? "noindex, follow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1");
    setMeta('meta[property="og:title"]', "property", "og:title", effectiveTitle);
    setMeta('meta[property="og:description"]', "property", "og:description", effectiveDescription);
    setMeta('meta[property="og:url"]', "property", "og:url", canonical);
    setMeta('meta[property="og:image"]', "property", "og:image", DEFAULT_OG_IMAGE);
    setMeta('meta[property="og:image:alt"]', "property", "og:image:alt", DEFAULT_OG_ALT);
    setMeta('meta[name="twitter:title"]', "name", "twitter:title", effectiveTitle);
    setMeta('meta[name="twitter:description"]', "name", "twitter:description", effectiveDescription);
    setMeta('meta[name="twitter:image"]', "name", "twitter:image", DEFAULT_OG_IMAGE);
    setLink("canonical", canonical);

    let schema = document.querySelector<HTMLScriptElement>("#seo-structured-data");
    if (!schema) {
      schema = document.createElement("script");
      schema.id = "seo-structured-data";
      schema.type = "application/ld+json";
      document.head.append(schema);
    }
    schema.textContent = JSON.stringify(structuredDataFor(entry.path));
  }, [pathname, title, description]);
  return null;
}
